import { Alert, Button, StatusBadge, Surface } from './ui/Primitives';
import React, { useEffect, useMemo, useState } from 'react';
import {
  deferOnboardingTeamInvitation,
  dismissOnboardingSetupPlan,
  getOnboarding,
} from '../services/onboardingApi';
import { stepProgress } from '../utils/onboardingFlow';
import { logEvent } from '../utils/logger';

const stepLabels = {
  goal: 'Choose your first caller outcome',
  experience: 'Personalize setup guidance',
  business_profile: 'Confirm your business profile',
  personalize: 'Set the greeting and voice',
  goal_setup: 'Finish your selected setup paths',
  call_recording: 'Choose call recording',
  review: 'Review your setup',
  phone_number: 'Choose a phone number',
  first_call: 'Complete your first call',
};

function OnboardingSetupPlan() {
  const [onboarding, setOnboarding] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deferringTeam, setDeferringTeam] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getOnboarding().then(result => {
      if (active) setOnboarding(result);
    }).catch(() => {
      // The overview remains usable if this optional panel cannot load.
    });
    return () => { active = false; };
  }, []);

  const tasks = useMemo(() => {
    if (!onboarding) return [];
    const readiness = onboarding.readiness || {};
    return [
      { label: 'Business profile confirmed', complete: Boolean(readiness.requirements?.businessProfile), to: '/onboarding' },
      { label: 'Greeting and voice selected', complete: Boolean(readiness.requirements?.greeting && readiness.requirements?.voice), to: '/onboarding' },
      { label: 'Receptionist number claimed', complete: Boolean(readiness.numberClaimed), to: '/onboarding' },
      { label: 'First successful call completed', complete: Boolean(readiness.firstCallCompleted), to: '/onboarding' },
    ];
  }, [onboarding]);

  if (!onboarding) return null;

  const completedCount = tasks.filter(task => task.complete).length;
  const progress = stepProgress(onboarding.currentStepKey, onboarding.steps);
  const isComplete = onboarding.status === 'completed';
  const setupPlan = onboarding.setupPlan || {
    state: isComplete ? 'complete' : 'in_progress',
    visible: true,
  };

  const teamInvitationSetup = onboarding.teamInvitationSetup || {};
  const showTeamInvitation = Boolean(
    isComplete
    && teamInvitationSetup.eligible
    && !teamInvitationSetup.complete
  );
  const showSetupPlan = setupPlan.visible && setupPlan.state !== 'retired';

  if (!showSetupPlan && !showTeamInvitation) return null;

  async function dismissPlan() {
    setDismissing(true);
    setError('');
    try {
      const result = await dismissOnboardingSetupPlan();
      setOnboarding(result);
      logEvent({ event: { label: 'onboarding_setup_plan_dismissed' } });
    } catch (_requestError) {
      setError('The setup card could not be dismissed. Try again in a moment.');
    } finally {
      setDismissing(false);
    }
  }

  async function copyNumber() {
    const number = onboarding.organization?.provisionedNumber;
    if (!number) return;
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      logEvent({ event: { label: 'onboarding_activation_number_copied' } });
    } catch (_copyError) {
      setError('The number could not be copied. Select it from the overview instead.');
    }
  }

  async function deferTeamInvitation() {
    setDeferringTeam(true);
    setError('');
    try {
      const result = await deferOnboardingTeamInvitation();
      setOnboarding(result);
      logEvent({ event: { label: 'onboarding_team_invitation_deferred' } });
    } catch (_requestError) {
      setError('The team invitation step could not be updated. Try again in a moment.');
    } finally {
      setDeferringTeam(false);
    }
  }

  if (isComplete) {
    const needsActivation = setupPlan.state === 'activation';
    return (
      <Surface className={`dashboard-setup-plan dashboard-setup-plan-compact${showSetupPlan ? '' : ' dashboard-setup-plan-team-only'}`}>
        {showSetupPlan && needsActivation ? (
          <div className="dashboard-setup-plan-heading">
            <div>
              <span className="say-eyebrow">Ready for customers</span>
              <h2>Your Receptionist has not handled a customer call yet</h2>
              <p>Share your Sayable number when you are ready. Your Receptionist is standing by.</p>
              {onboarding.organization?.provisionedNumber ? (
                <strong className="dashboard-activation-number">{onboarding.organization.provisionedNumber}</strong>
              ) : null}
            </div>
            <div className="dashboard-setup-plan-actions">
              <Button variant="secondary" onClick={copyNumber}>{copied ? 'Number copied' : 'Copy number'}</Button>
            </div>
          </div>
        ) : null}
        {showSetupPlan ? (
          <div className="dashboard-post-setup-task">
            <div className="dashboard-post-setup-icon" aria-hidden="true">✦</div>
            <div>
              <strong>Improve your knowledge</strong>
              <span>Add services, policies, documents, and other details so your Receptionist can give callers more complete answers.</span>
            </div>
            <div className="dashboard-post-setup-actions">
              <Button to="/knowledge" variant="secondary">Improve knowledge</Button>
              <Button variant="quiet" onClick={dismissPlan} disabled={dismissing} loading={dismissing} loadingLabel={"Updating…"}>{'Do this later'}</Button>
            </div>
          </div>
        ) : null}
        {showTeamInvitation ? (
          <div className="dashboard-post-setup-task">
            <div className="dashboard-post-setup-icon" aria-hidden="true">↗</div>
            <div>
              <strong>Invite your team</strong>
              <span>Give coworkers secure access and help Sayable route calls and deliver messages for specific people.</span>
            </div>
            <div className="dashboard-post-setup-actions">
              <Button to="/organization?invite=1" variant="secondary">Invite team members</Button>
              <Button variant="quiet" onClick={deferTeamInvitation} disabled={deferringTeam} loading={deferringTeam} loadingLabel={"Updating…"}>{'Do this later'}</Button>
            </div>
          </div>
        ) : null}
        {error ? <Alert tone="danger" title="Setup card needs attention">{error}</Alert> : null}
      </Surface>
    );
  }

  return (
    <Surface className="dashboard-setup-plan">
      <div className="dashboard-setup-plan-heading">
        <div>
          <span className="say-eyebrow">Your setup plan</span>
          <h2>{stepLabels[onboarding.currentStepKey]}</h2>
          <p>{`About ${Math.max(1, 8 - progress.number)} minutes left. Your progress is saved.`}</p>
        </div>
        <Button to="/onboarding">Resume setup</Button>
      </div>
      <div className="dashboard-setup-progress" aria-label={`${completedCount} of ${tasks.length} setup milestones complete`}>
        <progress value={completedCount} max={tasks.length}>{completedCount} of {tasks.length}</progress>
        <span>{completedCount} of {tasks.length} complete</span>
      </div>
      <ul className="dashboard-setup-tasks">
        {tasks.map(task => (
          <li key={task.label} className={task.complete ? 'complete' : ''}>
            <span aria-hidden="true">{task.complete ? '✓' : '○'}</span>
            <span>{task.label}</span>
            <StatusBadge status={task.complete ? 'success' : 'neutral'} dot={false}>{task.complete ? 'Done' : 'Next'}</StatusBadge>
          </li>
        ))}
      </ul>
    </Surface>
  );
}

export default OnboardingSetupPlan;
